import { supabase } from '../lib/supabase';
import ProgressMonitorService from './ProgressMonitorService';

/**
 * AlertDetectorService
 *
 * Detects problems and creates alerts:
 * - Sprint overruns (30%+ over estimated time)
 * - Behind schedule (1+ sprints behind)
 * - Milestones at risk
 * - Deadline risks (<48hrs with incomplete work)
 * - Burnout risk (low energy levels)
 * - Quality concerns (low quality ratings)
 *
 * Called:
 * - After each sprint completion
 * - Every 15 minutes during active sprint
 * - On milestone checks
 */
class AlertDetectorService {
  /**
   * Check for all alert conditions and create alerts
   * @param {string} userId - Student ID
   * @param {Object} snapshot - Current progress snapshot (optional)
   * @returns {Promise<Array>} Array of created alerts
   */
  async checkAndCreateAlerts(userId, snapshot = null) {
    try {
      // Get current progress if not provided
      if (!snapshot) {
        snapshot = await ProgressMonitorService.getCurrentProgress(userId);
      }

      if (!snapshot) {
        console.log('[AlertDetectorService] No snapshot available');
        return [];
      }

      const alerts = [];

      // Check for sprint overrun
      const overrunAlert = await this.checkSprintOverrun(userId, snapshot);
      if (overrunAlert) alerts.push(overrunAlert);

      // Check for behind schedule
      if (snapshot.sprints_behind > 0) {
        const behindAlert = await this.checkBehindSchedule(userId, snapshot);
        if (behindAlert) alerts.push(behindAlert);
      }

      // Check for milestones at risk
      const milestoneAlerts = await this.checkMilestonesAtRisk(userId, snapshot);
      alerts.push(...milestoneAlerts);

      // Check for deadline risks
      const deadlineAlerts = await this.checkDeadlineRisks(userId, snapshot);
      alerts.push(...deadlineAlerts);

      // Check for burnout risk
      const burnoutAlert = await this.checkBurnoutRisk(userId, snapshot);
      if (burnoutAlert) alerts.push(burnoutAlert);

      // Check for quality concerns
      const qualityAlert = await this.checkQualityConcern(userId, snapshot);
      if (qualityAlert) alerts.push(qualityAlert);

      // Save all alerts to database
      const savedAlerts = [];
      for (const alert of alerts) {
        const saved = await this.createAlert(alert);
        if (saved) savedAlerts.push(saved);
      }

      return savedAlerts;
    } catch (error) {
      console.error('[AlertDetectorService] Error checking alerts:', error);
      throw error;
    }
  }

  /**
   * Check if current sprint is running over time
   * @param {string} userId - Student ID
   * @param {Object} snapshot - Progress snapshot
   * @returns {Promise<Object|null>} Alert object or null
   */
  async checkSprintOverrun(userId, snapshot) {
    if (!snapshot.current_sprint_id) return null;

    try {
      // Get current sprint
      const { data: sprint, error } = await supabase
        .from('sprints')
        .select('*')
        .eq('id', snapshot.current_sprint_id)
        .single();

      if (error || !sprint) return null;

      // Only check if sprint is in progress
      if (sprint.status !== 'IN_PROGRESS') return null;

      // Check if sprint has been running
      if (!sprint.actual_start_time) return null;

      const now = new Date();
      const startTime = new Date(sprint.actual_start_time);
      const elapsedMinutes = (now - startTime) / 1000 / 60;

      // Check if 30% over estimated time
      const overrunThreshold = sprint.estimated_duration * 1.3;

      if (elapsedMinutes > overrunThreshold) {
        const minutesOver = Math.floor(elapsedMinutes - sprint.estimated_duration);

        return {
          user_id: userId,
          type: 'SPRINT_OVERRUN',
          severity: 'WARNING',
          title: 'Sprint Running Long',
          message: `Current sprint "${sprint.name}" is ${minutesOver} minutes over estimated time (${sprint.estimated_duration} min). Consider wrapping up soon.`,
          sprint_id: sprint.id,
          context: {
            estimated_duration: sprint.estimated_duration,
            elapsed_minutes: Math.floor(elapsedMinutes),
            minutes_over: minutesOver
          }
        };
      }

      return null;
    } catch (error) {
      console.error('[AlertDetectorService] Error checking sprint overrun:', error);
      return null;
    }
  }

  /**
   * Check if student is behind schedule
   * @param {string} userId - Student ID
   * @param {Object} snapshot - Progress snapshot
   * @returns {Promise<Object|null>} Alert object or null
   */
  async checkBehindSchedule(userId, snapshot) {
    // Check if alert already exists and is not dismissed
    const existingAlert = await this.findExistingAlert(
      userId,
      'BEHIND_SCHEDULE',
      false // not dismissed
    );

    if (existingAlert) {
      return null; // Don't create duplicate
    }

    let severity = 'INFO';
    if (snapshot.sprints_behind >= 2) severity = 'CRITICAL';
    else if (snapshot.sprints_behind === 1) severity = 'WARNING';

    const message = snapshot.sprints_behind === 1
      ? `You're 1 sprint behind schedule (${snapshot.minutes_behind} minutes). Recovery options available.`
      : `You're ${snapshot.sprints_behind} sprints behind schedule (${snapshot.minutes_behind} minutes). Consider adjusting your plan.`;

    return {
      user_id: userId,
      type: 'BEHIND_SCHEDULE',
      severity,
      title: 'Behind Schedule',
      message,
      context: {
        sprints_behind: snapshot.sprints_behind,
        minutes_behind: snapshot.minutes_behind,
        risk_level: snapshot.risk_level
      }
    };
  }

  /**
   * Check for milestones at risk
   * @param {string} userId - Student ID
   * @param {Object} snapshot - Progress snapshot
   * @returns {Promise<Array>} Array of alert objects
   */
  async checkMilestonesAtRisk(userId, snapshot) {
    try {
      const { data: milestones, error } = await supabase
        .from('milestones')
        .select('*, sprint_schedules!inner(user_id)')
        .eq('sprint_schedules.user_id', userId)
        .eq('status', 'UPCOMING');

      if (error) throw error;
      if (!milestones || milestones.length === 0) return [];

      const alerts = [];
      const now = new Date();

      for (const milestone of milestones) {
        const targetDate = new Date(milestone.target_date);
        const hoursUntil = (targetDate - now) / (1000 * 60 * 60);

        // Check if milestone is within 24 hours
        if (hoursUntil > 0 && hoursUntil <= 24) {
          // Check if required sprints are complete
          const { data: sprints, error: sprintsError } = await supabase
            .from('sprints')
            .select('id, status')
            .in('id', milestone.required_sprint_ids || []);

          if (sprintsError) continue;

          const incompleteSprints = sprints.filter(s => s.status !== 'COMPLETE');

          if (incompleteSprints.length > 0) {
            // Check if alert already exists
            const existingAlert = await this.findExistingAlert(
              userId,
              'MILESTONE_AT_RISK',
              false,
              milestone.id
            );

            if (!existingAlert) {
              alerts.push({
                user_id: userId,
                type: 'MILESTONE_AT_RISK',
                severity: 'CRITICAL',
                title: 'Milestone At Risk',
                message: `Milestone "${milestone.name}" is due in ${Math.floor(hoursUntil)} hours, but ${incompleteSprints.length} required sprint(s) are incomplete.`,
                milestone_id: milestone.id,
                context: {
                  milestone_name: milestone.name,
                  target_date: milestone.target_date,
                  hours_until: Math.floor(hoursUntil),
                  incomplete_sprints: incompleteSprints.length,
                  required_sprint_ids: milestone.required_sprint_ids
                }
              });
            }
          }
        }
      }

      return alerts;
    } catch (error) {
      console.error('[AlertDetectorService] Error checking milestones:', error);
      return [];
    }
  }

  /**
   * Check for deadline risks
   * @param {string} userId - Student ID
   * @param {Object} snapshot - Progress snapshot
   * @returns {Promise<Array>} Array of alert objects
   */
  async checkDeadlineRisks(userId, snapshot) {
    try {
      // Get schedule to check deadline
      const { data: schedule, error } = await supabase
        .from('sprint_schedules')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error || !schedule) return [];

      const now = new Date();
      const endDate = new Date(schedule.end_date);
      const hoursUntilDeadline = (endDate - now) / (1000 * 60 * 60);

      // Check if within 48 hours of deadline
      if (hoursUntilDeadline > 0 && hoursUntilDeadline <= 48) {
        // Check if there's incomplete work
        const incompleteSprints = snapshot.sprints_total - snapshot.sprints_completed;

        if (incompleteSprints > 0) {
          // Check if alert already exists
          const existingAlert = await this.findExistingAlert(
            userId,
            'DEADLINE_RISK',
            false
          );

          if (!existingAlert) {
            const severity = hoursUntilDeadline <= 24 ? 'EMERGENCY' : 'CRITICAL';

            return [{
              user_id: userId,
              type: 'DEADLINE_RISK',
              severity,
              title: 'Deadline Approaching',
              message: `Deadline is in ${Math.floor(hoursUntilDeadline)} hours with ${incompleteSprints} sprint(s) remaining. Immediate action required.`,
              context: {
                hours_until_deadline: Math.floor(hoursUntilDeadline),
                incomplete_sprints: incompleteSprints,
                end_date: schedule.end_date
              }
            }];
          }
        }
      }

      return [];
    } catch (error) {
      console.error('[AlertDetectorService] Error checking deadline risks:', error);
      return [];
    }
  }

  /**
   * Check for burnout risk based on session metrics
   * @param {string} userId - Student ID
   * @param {Object} snapshot - Progress snapshot
   * @returns {Promise<Object|null>} Alert object or null
   */
  async checkBurnoutRisk(userId, snapshot) {
    // TODO: Implement based on session_metrics table
    // Check for:
    // - Multiple consecutive low energy_level ratings
    // - Long stretches without breaks
    // - Declining task completion rates

    if (snapshot.burnout_risk && snapshot.burnout_risk > 70) {
      const existingAlert = await this.findExistingAlert(
        userId,
        'BURNOUT_RISK',
        false
      );

      if (!existingAlert) {
        return {
          user_id: userId,
          type: 'BURNOUT_RISK',
          severity: 'WARNING',
          title: 'Burnout Risk Detected',
          message: 'Your energy levels have been consistently low. Consider taking a longer break or adjusting your schedule.',
          context: {
            burnout_risk: snapshot.burnout_risk
          }
        };
      }
    }

    return null;
  }

  /**
   * Check for quality concerns
   * @param {string} userId - Student ID
   * @param {Object} snapshot - Progress snapshot
   * @returns {Promise<Object|null>} Alert object or null
   */
  async checkQualityConcern(userId, snapshot) {
    // TODO: Implement based on session_metrics and deliverables quality ratings

    if (snapshot.average_task_quality && snapshot.average_task_quality < 3.0) {
      const existingAlert = await this.findExistingAlert(
        userId,
        'QUALITY_CONCERN',
        false
      );

      if (!existingAlert) {
        return {
          user_id: userId,
          type: 'QUALITY_CONCERN',
          severity: 'WARNING',
          title: 'Quality Concern',
          message: `Task quality ratings are below target (avg ${snapshot.average_task_quality.toFixed(1)}/5). Consider slowing down to maintain quality.`,
          context: {
            average_quality: snapshot.average_task_quality
          }
        };
      }
    }

    return null;
  }

  /**
   * Create an alert in the database
   * @param {Object} alert - Alert object
   * @returns {Promise<Object>} Created alert
   */
  async createAlert(alert) {
    try {
      const { data, error } = await supabase
        .from('alerts')
        .insert({
          ...alert,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      console.log(`[AlertDetectorService] Alert created: ${alert.type} - ${alert.title}`);
      return data;
    } catch (error) {
      console.error('[AlertDetectorService] Error creating alert:', error);
      return null;
    }
  }

  /**
   * Find existing alert to avoid duplicates
   * @param {string} userId - Student ID
   * @param {string} type - Alert type
   * @param {boolean} isDismissed - Whether to check dismissed status
   * @param {string} milestoneId - Optional milestone ID
   * @returns {Promise<Object|null>} Existing alert or null
   */
  async findExistingAlert(userId, type, isDismissed = false, milestoneId = null) {
    try {
      let query = supabase
        .from('alerts')
        .select('*')
        .eq('user_id', userId)
        .eq('type', type)
        .eq('is_dismissed', isDismissed);

      if (milestoneId) {
        query = query.eq('milestone_id', milestoneId);
      }

      const { data, error } = await query.order('created_at', { ascending: false }).limit(1);

      if (error) throw error;

      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error('[AlertDetectorService] Error finding existing alert:', error);
      return null;
    }
  }

  /**
   * Get all alerts for a user
   * @param {string} userId - Student ID
   * @param {Object} options - Filter options
   * @returns {Promise<Array>} Array of alerts
   */
  async getAlerts(userId, options = {}) {
    try {
      let query = supabase
        .from('alerts')
        .select('*')
        .eq('user_id', userId);

      if (options.severity) {
        query = query.eq('severity', options.severity);
      }

      if (options.unread !== undefined) {
        query = query.eq('is_read', !options.unread);
      }

      if (options.dismissed !== undefined) {
        query = query.eq('is_dismissed', options.dismissed);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('[AlertDetectorService] Error getting alerts:', error);
      throw error;
    }
  }

  /**
   * Mark alert as read
   * @param {string} alertId - Alert ID
   * @returns {Promise<Object>} Updated alert
   */
  async markAsRead(alertId) {
    try {
      const { data, error } = await supabase
        .from('alerts')
        .update({ is_read: true })
        .eq('id', alertId)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('[AlertDetectorService] Error marking alert as read:', error);
      throw error;
    }
  }

  /**
   * Dismiss an alert
   * @param {string} alertId - Alert ID
   * @returns {Promise<Object>} Updated alert
   */
  async dismissAlert(alertId) {
    try {
      const { data, error } = await supabase
        .from('alerts')
        .update({
          is_dismissed: true,
          dismissed_at: new Date().toISOString()
        })
        .eq('id', alertId)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('[AlertDetectorService] Error dismissing alert:', error);
      throw error;
    }
  }
}

export default new AlertDetectorService();
